import { AttributionControl, MapContainer, Marker, Popup, TileLayer } from 'react-leaflet';
import { Icon, LatLngExpression, LatLngTuple } from 'leaflet';
import { FC, useState } from 'react';
import compact from 'lodash/compact';
import { GetAllDataQuery } from 'queries/getAllData';
import { Unpacked } from '@/utils';
import cn from 'classnames';
import { useLanguage } from '@/contexts/LanguageContext';

const initialPosition: LatLngExpression = [51.51410864757791, 7.464467542895241];

const DortmundBoundaries: [LatLngTuple, LatLngTuple] = [
  [51.617190590861775, 7.192626838458426],
  [51.3716154970438, 7.705285150108021],
];

export type MapProps = {
  className?: string;
  markers?: Array<{ position: LatLngExpression } & Unpacked<GetAllDataQuery['allLocations']>>;
};

export const Map: FC<MapProps> = ({ className, markers }) => {
  const { t } = useLanguage();
  const [activePosition, setActivePosition] = useState<LatLngExpression | null>(null);
  const checkPosition = (pos: LatLngExpression) => JSON.stringify(activePosition) === JSON.stringify(pos);

  const validMarkers = compact(markers).filter(({ position }) => {
    const isNumber = (num) => typeof num === 'number' && !Number.isNaN(num);
    return isNumber(position[0]) && isNumber(position[1]);
  });

  const clusteredMarkers = validMarkers.reduce((acc, marker) => {
    const key = `${marker.latitude},${marker.longitude}`;
    if (acc[key]) {
      return { ...acc, [key]: [...acc[key], marker] };
    }
    return { ...acc, [key]: [marker] };
  }, {});

  return (
    <MapContainer
      center={initialPosition}
      zoom={12}
      maxZoom={18}
      minZoom={10}
      scrollWheelZoom
      className={className}
      maxBounds={DortmundBoundaries}
      maxBoundsViscosity={1}
      attributionControl={false}
    >
      <TileLayer
        attribution='<a href="https://www.maptiler.com/copyright/" target="_blank">&copy; MapTiler</a> <a href="https://www.openstreetmap.org/copyright" target="_blank">&copy; OpenStreetMap</a>'
        url="/api/tiles/{z}/{x}/{y}.png"
      />
      {Object.entries(clusteredMarkers).map(([latLngString, markers]) => {
        const position = latLngString.split(',').map(Number) as LatLngExpression;
        return (
          <Marker
            position={position}
            icon={
              new Icon({
                iconUrl: activePosition && checkPosition(position) ? '/static/leaflet/Pin_active.svg' : '/static/leaflet/Pin_inactive.svg',
                iconSize: [22, 34],
                popupAnchor: [0, -5],
              })
            }
            eventHandlers={{
              click: ({ latlng: { lat, lng } }) => setActivePosition([lat, lng]),
              popupclose: () => setActivePosition(null),
            }}
          >
            <Popup className="rounded-xl">
              {/* @ts-ignore */}
              {markers.map(({ location_name, street, zip, city, house_number }, index) => {
                return (
                  <>
                    <p className={cn('font-sans', { 'pt-4 border-t': index > 0 })}>
                      <span className="font-bold">{location_name}</span>
                      <br />
                      {street} {house_number}
                      <br />
                      {zip} {city}
                    </p>
                    <a href={`#${location_name}-${street}-${house_number}`.replace(/\s+/g, '_')}>{t.location.moreInfo}</a>
                  </>
                );
              })}
            </Popup>
          </Marker>
        );
      })}
      <AttributionControl prefix={false} />
    </MapContainer>
  );
};
